import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  getFirestore, 
  collection, 
  getDocs, 
  doc, 
  getDoc 
} from 'firebase/firestore';
import { 
  Search, 
  BookOpen, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  MoreVertical,
  Download
} from 'lucide-react';

const firestore = getFirestore();

const AdminLearnerDashboard = () => {
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, completed, not-started

  // Constants for the course - consistent with EducationDashboard.js
  const TOTAL_LESSONS = 6; 

  useEffect(() => {
    const fetchLearnerData = async () => {
      try {
        setLoading(true);
        // 1. Fetch all registrations from FormRegistration
        const registrationsRef = collection(firestore, 'FormRegistration');
        const registrationsSnap = await getDocs(registrationsRef);
        
        
        // 2. Process each registration and fetch their course progress
        const promises = registrationsSnap.docs.map(async (regDoc) => {
          const regData = regDoc.data();
          const firebaseUid = regData.firebaseUid; // The link to the user
          
          let progressData = {
            currentLessonIndex: 0,
            maxLessonReachedIndex: 0,
            lastAccessed: null,
            checklist: {}
          };
          
          // Only attempt to fetch progress if we have a UID to link to
          if (firebaseUid) {
            try {
              const progressRef = doc(firestore, 'users', firebaseUid, 'course_progress', 'retail_quant_launchpad');
              const progressSnap = await getDoc(progressRef);
              
              if (progressSnap.exists()) {
                progressData = progressSnap.data();
              }
            } catch (err) {
              console.error(`Error fetching progress for ${regData.name}:`, err);
            }
          }
          
          // Calculate stats
          const progressPercentage = Math.min(100, Math.round((progressData.maxLessonReachedIndex / TOTAL_LESSONS) * 100));
          
          let status = 'not-started';
          if (progressPercentage === 100) status = 'completed';
          else if (progressPercentage > 0) status = 'active';
          
            return {
            id: regDoc.id,
            name: regData.name || 'Unknown Name',
            email: regData.emailAddress || 'No Email',
            cohort: regData.classDays || 'N/A',
            registeredAt: regData.createdAt || regData.submittedAt || null, // Capture registration date
            firebaseUid,
            ...progressData,
            progressPercentage,
            status
          };
        });
        
        const results = await Promise.all(promises);
        
        // Sort results: Active users first, then by last accessed
        results.sort((a, b) => {
            // Prioritize active users
            if (a.status === 'active' && b.status !== 'active') return -1;
            if (b.status === 'active' && a.status !== 'active') return 1;
            
            // Then sort by registration date (newest first)
            const regTimeA = a.registeredAt?.toDate ? a.registeredAt.toDate().getTime() : (a.registeredAt ? new Date(a.registeredAt).getTime() : 0);
            const regTimeB = b.registeredAt?.toDate ? b.registeredAt.toDate().getTime() : (b.registeredAt ? new Date(b.registeredAt).getTime() : 0);
            
            // If checking "Last Accessed", keep that logic too if preferred, but usually sorting by registration is nice. 
            // The previous logic sorted by lastAccessed. Let's keep lastAccessed prioritization for active users, but maybe fallback to registration date?
            // Actually, let's keep the user's existing sort logic (active first, then lastAccessed), but update the fallback to be registration date instead of 0
            
            const timeA = a.lastAccessed?.toMillis ? a.lastAccessed.toMillis() : 0;
            const timeB = b.lastAccessed?.toMillis ? b.lastAccessed.toMillis() : 0;
            
            if (timeA !== timeB) return timeB - timeA;
            
            return regTimeB - regTimeA;
        });

        setLearners(results);
      } catch (error) {
        console.error("Error creating breakdown:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLearnerData();
  }, []);

  // Filter logic
  const filteredLearners = learners.filter(learner => {
    const matchesSearch = 
      learner.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      learner.email.toLowerCase().includes(searchQuery.toLowerCase());
      
    const matchesFilter = 
      filterStatus === 'all' || 
      learner.status === filterStatus;
      
    return matchesSearch && matchesFilter;
  });

  // Calculate generic stats
  const stats = {
    total: learners.length,
    active: learners.filter(l => l.status === 'active').length,
    completed: learners.filter(l => l.status === 'completed').length,
    notStarted: learners.filter(l => l.status === 'not-started').length
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (learners.length === 0) return;
    
    // Define headers
    const headers = ['Name', 'Email', 'Cohort', 'Date Registered', 'UID', 'Progress (%)', 'Current Module', 'Status', 'Last Active'];
    
    // Convert data to CSV format
    const csvContent = [
        headers.join(','),
        ...learners.map(l => {
            const lastActive = l.lastAccessed ? new Date(l.lastAccessed.toDate ? l.lastAccessed.toDate() : l.lastAccessed).toISOString() : 'Never';
            const dateRegistered = l.registeredAt ? new Date(l.registeredAt.toDate ? l.registeredAt.toDate() : l.registeredAt).toISOString() : 'Unknown';
            return [
                `"${l.name}"`, 
                `"${l.email}"`, 
                `"${l.cohort}"`, 
                `"${dateRegistered}"`,
                `"${l.firebaseUid || ''}"`,
                l.progressPercentage,
                l.maxLessonReachedIndex,
                l.status,
                lastActive
            ].join(',');
        })
    ].join('\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `learner_progress_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-10 pt-24 font-sans text-gray-900">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Learner Progress Dashboard</h1>
            <p className="text-gray-500 mt-1">Monitor student progress for Retail Quant Launchpad</p>
          </div>
          <button 
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 text-sm font-medium transition-colors"
          >
            <Download size={16} />
            Export CSV
          </button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Students" value={stats.total} icon={BookOpen} color="blue" />
            <StatCard label="Active Users" value={stats.active} icon={Clock} color="amber" />
            <StatCard label="Completed" value={stats.completed} icon={CheckCircle} color="green" />
            <StatCard label="Not Started" value={stats.notStarted} icon={AlertCircle} color="gray" />
        </div>

        {/* Controls & Search */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col md:flex-row gap-4 justify-between items-center shadow-sm">
            <div className="relative w-full md:w-96">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input 
                    type="text" 
                    placeholder="Search by name or email..." 
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>
            
            <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0">
                <FilterButton label="All Students" active={filterStatus === 'all'} onClick={() => setFilterStatus('all')} />
                <FilterButton label="Active" active={filterStatus === 'active'} onClick={() => setFilterStatus('active')} count={stats.active} />
                <FilterButton label="Completed" active={filterStatus === 'completed'} onClick={() => setFilterStatus('completed')} count={stats.completed} />
                <FilterButton label="Not Started" active={filterStatus === 'not-started'} onClick={() => setFilterStatus('not-started')} count={stats.notStarted} />
            </div>
        </div>

        {/* Use Card Grid for better readability on dashboard */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500 font-semibold">
                            <th className="px-6 py-4">Student</th>
                            <th className="px-6 py-4">Cohort</th>
                            <th className="px-6 py-4">Date Registered</th>
                            <th className="px-6 py-4">Progress</th>
                            <th className="px-6 py-4">Last Active</th>
                            <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredLearners.length > 0 ? (
                            filteredLearners.map((learner) => (
                                <TableRow key={learner.id} learner={learner} />
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                    No students found matching your filters.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            
            {/* Pagination footer could go here */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 text-sm text-gray-500 flex justify-between items-center">
                <span>Showing {filteredLearners.length} of {learners.length} students</span>
                {/* Simple pagination placeholder */}
                <div className="flex gap-2">
                    <button className="px-3 py-1 border border-gray-200 rounded bg-white disabled:opacity-50" disabled>Previous</button>
                    <button className="px-3 py-1 border border-gray-200 rounded bg-white disabled:opacity-50" disabled>Next</button>
                </div>
            </div>
        </div>

      </div>
    </div>
  );
};

// Sub-components for cleanliness

const StatCard = ({ label, value, icon: Icon, color }) => {
    // Determine status colors for background and text
    const colors = {
        blue: 'text-blue-600 bg-blue-50 border-blue-100',
        green: 'text-green-600 bg-green-50 border-green-100',
        amber: 'text-amber-600 bg-amber-50 border-amber-100',
        gray: 'text-gray-600 bg-gray-50 border-gray-100',
    };

    return (
        <div className={`p-5 rounded-xl border ${colors[color].split(' ').pop()} bg-white shadow-sm flex items-start justify-between`}>
            <div>
                <p className="text-sm font-medium text-gray-500 mb-1">{label}</p>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
            <div className={`p-2 rounded-lg ${colors[color]}`}>
                <Icon size={20} />
            </div>
        </div>
    );
};

const FilterButton = ({ label, active, onClick, count }) => (
    <button 
        onClick={onClick}
        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap
            ${active 
                ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
    >
        {label}
        {count !== undefined && <span className={`ml-2 text-xs ${active ? 'text-blue-200' : 'text-gray-500'}`}>({count})</span>}
    </button>
);

const TableRow = ({ learner }) => {
    // Format timestamp
    const formatDate = (timestamp) => {
        if (!timestamp) return 'Never';
        // Handle Firestore timestamp
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date);
    };

    return (
        <motion.tr 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="hover:bg-gray-50/80 transition-colors group"
        >
            <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm shadow-sm">
                        {learner.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                        <div className="font-semibold text-gray-900">{learner.name}</div>
                        <div className="text-xs text-gray-500">{learner.email}</div>
                    </div>
                </div>
            </td>
            <td className="px-6 py-4">
                <div className="text-sm text-gray-700">{learner.cohort}</div>
                <div className="text-xs text-gray-400 font-mono mt-0.5 truncate w-24" title={learner.firebaseUid}>
                    {learner.firebaseUid ? <span className="text-green-600 flex items-center gap-1">Linked <CheckCircle size={10} /></span> : <span className="text-red-400">Unlinked</span>}
                </div>
            </td>
            <td className="px-6 py-4">
                <div className="text-sm text-gray-600">
                    {learner.registeredAt ? formatDate(learner.registeredAt) : <span className="text-gray-400 italic">Unknown</span>}
                </div>
            </td>
            <td className="px-6 py-4">
                <div className="w-full max-w-[140px]">
                    <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium text-gray-700">{learner.progressPercentage}%</span>
                        <span className="text-gray-400">Mod {learner.maxLessonReachedIndex}</span>
                    </div>
                    <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div 
                            className={`h-full rounded-full ${
                                learner.progressPercentage === 100 ? 'bg-green-500' : 
                                learner.progressPercentage === 0 ? 'bg-gray-300' : 'bg-blue-500'
                            }`} 
                            style={{ width: `${learner.progressPercentage}%` }}
                        ></div>
                    </div>
                </div>
            </td>
            <td className="px-6 py-4">
                <div className="text-sm text-gray-600 flex items-center gap-2">
                    <Clock size={14} className="text-gray-400" />
                    {formatDate(learner.lastAccessed)}
                </div>
            </td>
            <td className="px-6 py-4 text-right">
                <button className="p-2 hover:bg-gray-200 rounded-full text-gray-400 hover:text-gray-600 transition-colors">
                    <MoreVertical size={18} />
                </button>
            </td>
        </motion.tr>
    );
};

export default AdminLearnerDashboard;
